import { useEffect, useRef, useState } from "react";
import Sidebar from "components/common/Sidebar";
import Header from "components/common/Header";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

// Canonical base URL for the Citations API. The citation routes are mounted
// under the main API gateway at /IpVijil/citation. Kept as a single constant
// so it can be swapped for a resolved env value later.
const BASE_URL = "https://api.demo.ipvijil.ai/IpVijil/citation";

const SECTIONS = [
  { id: "introduction", label: "Introduction" },
  { id: "authentication", label: "Authentication" },
  { id: "errors", label: "Errors" },
  { id: "analyze", label: "Analyze citations" },
  { id: "health", label: "Health check" },
];

/* ----------------------------- Small pieces ----------------------------- */

const MethodBadge = ({ method }: { method: string }) => {
  const isGet = method === "GET";
  return (
    <span
      className={`text-xs font-interSemiBold px-2 py-0.5 rounded tracking-wide ${
        isGet
          ? "bg-[#EAF0FB] text-[#1751D0]"
          : "bg-[#ECFDF3] text-[#067647]"
      }`}
    >
      {method}
    </span>
  );
};

const CodeBlock = ({
  tabs,
}: {
  tabs: { label: string; code: string }[];
}) => {
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const current = tabs[active] || tabs[0];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(current.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="rounded-lg overflow-hidden border border-[#1f2a44] bg-[#0b1020]">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#1f2a44] bg-[#0d1326]">
        <div className="flex items-center gap-x-1">
          {tabs.map((t, i) => (
            <button
              key={t.label}
              type="button"
              onClick={() => setActive(i)}
              className={`text-xs font-interMedium px-2.5 py-1 rounded transition-colors ${
                i === active
                  ? "bg-[#1f2a44] text-white"
                  : "text-[#94a3b8] hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="text-[#94a3b8] hover:text-white flex items-center gap-x-1 text-xs"
          title="Copy"
        >
          {copied ? (
            <CheckIcon sx={{ width: 15, height: 15 }} />
          ) : (
            <ContentCopyIcon sx={{ width: 15, height: 15 }} />
          )}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-[12.5px] leading-relaxed text-[#e2e8f0] font-mono whitespace-pre">
        <code>{current.code}</code>
      </pre>
    </div>
  );
};

const SectionHeading = ({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) => (
  <h2
    id={id}
    className="text-2xl font-interSemiBold text-[#101828] scroll-mt-6 mb-3"
  >
    {children}
  </h2>
);

/* ------------------------------- Code text ------------------------------- */

const healthCurl = `curl "${BASE_URL}/health"`;

const authCurl = `curl "${BASE_URL}/health" \\
  -H "X-API-Key: $IPVIJIL_API_KEY"`;

const analyzeCurl = `curl -X POST "${BASE_URL}/analyze" \\
  -H "X-API-Key: $IPVIJIL_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "patent_number": "US10123456",
    "claim_elements": [
      {
        "claim_number": "claim1",
        "elements": [
          "a processor configured to execute instructions",
          "a memory storing data structures"
        ]
      }
    ],
    "target_patent_number": "US10987654"
  }'`;

const analyzePython = `import requests

resp = requests.post(
    "${BASE_URL}/analyze",
    headers={
        "X-API-Key": "YOUR_API_KEY",
        "Content-Type": "application/json",
    },
    json={
        "patent_number": "US10123456",
        "claim_elements": [
            {
                "claim_number": "claim1",
                "elements": [
                    "a processor configured to execute instructions",
                    "a memory storing data structures",
                ],
            }
        ],
        "target_patent_number": "US10987654",
    },
)
resp.raise_for_status()
print(resp.json())`;

const analyzeResponse = `{
  "claim_analyses": [
    {
      "claim_number": "claim1",
      "elements": [
        {
          "element": "a processor configured to execute instructions",
          "is_match": true,
          "match_percentage": 85,
          "reason": "The target patent describes similar processor configuration in section 3..."
        },
        {
          "element": "a memory storing data structures",
          "is_match": true,
          "match_percentage": 78,
          "reason": "Memory storage is discussed in the target patent's disclosure..."
        }
      ],
      "overall_score": 81.5
    }
  ],
  "citations": [
    { "number": 1, "description": "Section 3, paragraphs 5-8 discussing processor configuration", "patentId": "US10987654" },
    { "number": 2, "description": "Section 4, paragraphs 2-4 discussing memory structures", "patentId": "US10987654" }
  ]
}`;

const healthPython = `import requests

print(requests.get("${BASE_URL}/health").json())`;

const healthResponse = `{ "status": "ok", "service": "citation-api" }`;

const errorExamples = `// 401 — invalid or missing API key
{ "detail": "Invalid API Key" }

// 422 — request validation error
{
  "detail": [
    { "loc": ["body", "claim_elements"], "msg": "field required", "type": "value_error.missing" }
  ]
}

// 500 — catch-all processing failure
{
  "detail": {
    "status_code": 500,
    "message": "Citation analysis failed.",
    "error": "<exception message>",
    "stack": "<traceback>"
  }
}`;

const ERROR_ROWS = [
  {
    status: "400",
    name: "Missing source patent number",
    desc: "patent_number is null or empty.",
  },
  {
    status: "400",
    name: "Missing target specification",
    desc: "Neither target_patent_number nor target_url supplied.",
  },
  {
    status: "400",
    name: "No valid claim elements",
    desc: "All elements for a claim are empty/whitespace and auto-extraction failed.",
  },
  {
    status: "401",
    name: "Invalid API key",
    desc: "X-API-Key missing, malformed, or not recognized. Includes a WWW-Authenticate: API Key header.",
  },
  {
    status: "401",
    name: "API key mapping not found",
    desc: "Valid key but no team/project/user mapping. Contact your administrator.",
  },
  {
    status: "422",
    name: "Request validation error",
    desc: "Malformed JSON or fields fail validation (e.g. missing claim_elements).",
  },
  {
    status: "500",
    name: "Source patent processing failed",
    desc: "Source patent could not be fetched or indexed.",
  },
  {
    status: "500",
    name: "Target patent processing failed",
    desc: "Target patent could not be fetched or indexed.",
  },
  {
    status: "500",
    name: "Target URL processing failed",
    desc: "Target URL could not be fetched, extracted, or indexed.",
  },
  {
    status: "500",
    name: "Citation analysis failed",
    desc: "Catch-all for unhandled errors after validation passes.",
  },
];

const REQUEST_FIELDS = [
  {
    name: "patent_number",
    type: "string",
    required: "Required",
    desc: 'Source patent number, e.g. "US10123456". Returns 400 if omitted.',
  },
  {
    name: "claim_elements",
    type: "array<ClaimElement>",
    required: "Required",
    desc: "Claim elements to analyze. Each ClaimElement has claim_number (string, e.g. \"claim1\") and elements (array of element description strings).",
  },
  {
    name: "target_patent_number",
    type: "string",
    required: "Required",
    desc: 'Target patent to compare against, e.g. "US10987654". At least one target field is required.',
  },
];

/* ------------------------------- The page ------------------------------- */

const ApiReference = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState("introduction");
  const [copiedBase, setCopiedBase] = useState(false);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
          );
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { root: container, rootMargin: "0px 0px -70% 0px", threshold: 0 }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const goTo = (id: string) => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const copyBaseUrl = async () => {
    try {
      await navigator.clipboard.writeText(BASE_URL);
      setCopiedBase(true);
      setTimeout(() => setCopiedBase(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="dashboard-wrapper flex">
      <Sidebar />
      <div className="w-[calc(100%_-_72px)] ml-auto">
        <Header type={"settings"} />

        <div className="flex h-[calc(100vh_-_70px)]">
          {/* Docs left nav */}
          <nav className="w-60 shrink-0 border-r border-[#EAECF0] overflow-y-auto py-6 px-3">
            <p className="text-xs font-interSemiBold text-[#101828] px-3 mb-2 uppercase tracking-wide">
              API Reference
            </p>
            <ul className="space-y-0.5">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => goTo(s.id)}
                    className={`block w-full text-left text-sm rounded-md py-2 px-3 transition-colors ${
                      activeId === s.id
                        ? "text-[#1751D0] bg-[#F5F8FD] font-interSemiBold"
                        : "text-[#667085] hover:text-[#1751D0] hover:bg-[#F5F8FD] font-interMedium"
                    }`}
                  >
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Content */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-8 py-8 scrollbar-adjust"
          >
            <div className="max-w-[1080px] mx-auto pb-24">
              {/* Introduction */}
              <section className="mb-14">
                <SectionHeading id="introduction">
                  Citations API
                </SectionHeading>
                <p className="text-sm text-[#475467] leading-relaxed max-w-[640px]">
                  The Citations API analyzes a source patent&apos;s claim
                  elements against a target patent and returns per-element match
                  scoring plus supporting citations. Send a list of claim
                  elements and a target patent number; receive a structured
                  analysis with match percentages, human-readable reasons, an
                  overall score, and a citation list.
                </p>

                <div className="mt-5 max-w-[640px]">
                  <div className="rounded-lg overflow-hidden border border-[#1f2a44] bg-[#0b1020]">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-[#1f2a44] bg-[#0d1326]">
                      <span className="text-xs font-interSemiBold text-[#94a3b8] tracking-wide uppercase">
                        Base URL
                      </span>
                      <button
                        type="button"
                        onClick={copyBaseUrl}
                        className="text-[#94a3b8] hover:text-white flex items-center gap-x-1 text-xs"
                        title="Copy"
                      >
                        {copiedBase ? (
                          <CheckIcon sx={{ width: 15, height: 15 }} />
                        ) : (
                          <ContentCopyIcon sx={{ width: 15, height: 15 }} />
                        )}
                        {copiedBase ? "Copied" : "Copy"}
                      </button>
                    </div>
                    <div className="p-4 text-[13px] text-[#e2e8f0] font-mono">
                      {BASE_URL}
                    </div>
                  </div>
                  <p className="text-xs text-[#667085] mt-2 leading-relaxed">
                    All requests are JSON over HTTPS. Every endpoint except{" "}
                    <code className="font-mono text-[#101828]">/health</code>{" "}
                    requires an API key (see Authentication).
                  </p>
                </div>
              </section>

              {/* Authentication */}
              <section className="mb-14">
                <SectionHeading id="authentication">
                  Authentication
                </SectionHeading>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 items-start">
                  <div className="text-sm text-[#475467] leading-relaxed space-y-3">
                    <p>
                      Authenticate every request by sending your key in the{" "}
                      <span className="font-interSemiBold text-[#101828]">
                        X-API-Key
                      </span>{" "}
                      request header. The header name is case-sensitive — use{" "}
                      <code className="font-mono text-[#101828]">
                        X-API-Key
                      </code>
                      , not <code className="font-mono">x-api-key</code>. A
                      missing or invalid key returns{" "}
                      <code className="font-mono text-[#101828]">
                        401 Invalid API Key
                      </code>
                      .
                    </p>
                    <p>
                      Keys are provisioned per team under{" "}
                      <span className="font-interMedium text-[#101828]">
                        Settings → API
                      </span>{" "}
                      and map internally to your team, project, and user. Keep
                      your key secret; never embed it in client-side code.
                    </p>
                  </div>
                  <CodeBlock tabs={[{ label: "cURL", code: authCurl }]} />
                </div>
              </section>

              {/* Errors */}
              <section className="mb-14">
                <SectionHeading id="errors">Errors</SectionHeading>
                <p className="text-sm text-[#475467] leading-relaxed max-w-[640px]">
                  The API uses conventional HTTP status codes.{" "}
                  <code className="font-mono text-[#101828]">2xx</code> indicates
                  success, <code className="font-mono text-[#101828]">4xx</code>{" "}
                  indicates a client or validation problem (bad/missing fields or
                  a bad key), and{" "}
                  <code className="font-mono text-[#101828]">5xx</code> indicates
                  a processing failure inside the service. Most errors return a
                  single <code className="font-mono text-[#101828]">detail</code>{" "}
                  string; <code className="font-mono text-[#101828]">422</code>{" "}
                  validation errors return an array of field-level objects.
                </p>

                <div className="mt-5 border border-[#EAECF0] rounded-lg overflow-hidden">
                  <table className="w-full text-left">
                    <thead className="bg-[#F9FAFB]">
                      <tr>
                        <th className="text-xs font-interSemiBold text-[#475467] py-2.5 px-4 w-20">
                          Status
                        </th>
                        <th className="text-xs font-interSemiBold text-[#475467] py-2.5 px-4 w-56">
                          Name
                        </th>
                        <th className="text-xs font-interSemiBold text-[#475467] py-2.5 px-4">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ERROR_ROWS.map((row, i) => (
                        <tr
                          key={`${row.status}-${row.name}`}
                          className={i % 2 ? "bg-[#FCFCFD]" : "bg-white"}
                        >
                          <td className="py-2.5 px-4 align-top">
                            <span className="text-xs font-mono font-interSemiBold text-[#101828]">
                              {row.status}
                            </span>
                          </td>
                          <td className="py-2.5 px-4 align-top text-sm text-[#101828] font-interMedium">
                            {row.name}
                          </td>
                          <td className="py-2.5 px-4 align-top text-sm text-[#475467]">
                            {row.desc}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-5 max-w-[640px]">
                  <p className="text-sm font-interSemiBold text-[#344054] mb-2">
                    Example error bodies
                  </p>
                  <CodeBlock tabs={[{ label: "JSON", code: errorExamples }]} />
                </div>
              </section>

              {/* Analyze citations */}
              <section className="mb-14">
                <div className="flex items-center gap-x-3 mb-1">
                  <SectionHeading id="analyze">Analyze citations</SectionHeading>
                </div>
                <div className="flex items-center gap-x-2 mb-4">
                  <MethodBadge method="POST" />
                  <code className="text-sm font-mono text-[#101828]">
                    /IpVijil/citation/analyze
                  </code>
                  <span className="text-xs text-[#667085]">· Auth required</span>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 items-start">
                  {/* Left: description + tables */}
                  <div>
                    <p className="text-sm text-[#475467] leading-relaxed mb-5">
                      Analyzes the supplied claim elements of a source patent
                      against a target patent. Returns per-element match results
                      (match percentage and reason), an overall score, and a
                      list of supporting citations.
                    </p>

                    <p className="text-sm font-interSemiBold text-[#344054] mb-2">
                      Request body
                    </p>
                    <div className="border border-[#EAECF0] rounded-lg overflow-hidden mb-6">
                      <table className="w-full text-left">
                        <tbody>
                          {REQUEST_FIELDS.map((f, i) => (
                            <tr
                              key={f.name}
                              className={
                                i % 2 ? "bg-[#FCFCFD]" : "bg-white"
                              }
                            >
                              <td className="py-2.5 px-4 align-top">
                                <code className="text-xs font-mono font-interSemiBold text-[#101828]">
                                  {f.name}
                                </code>
                                <div className="text-[11px] text-[#667085] mt-0.5">
                                  {f.type}
                                </div>
                                <div className="text-[11px] text-[#B54708] mt-0.5">
                                  {f.required}
                                </div>
                              </td>
                              <td className="py-2.5 px-4 align-top text-sm text-[#475467]">
                                {f.desc}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <p className="text-sm font-interSemiBold text-[#344054] mb-2">
                      Response
                    </p>
                    <p className="text-sm text-[#475467] leading-relaxed">
                      Returns a{" "}
                      <code className="font-mono text-[#101828]">
                        CitationResponse
                      </code>{" "}
                      with{" "}
                      <code className="font-mono text-[#101828]">
                        claim_analyses
                      </code>{" "}
                      (per claim: an{" "}
                      <code className="font-mono text-[#101828]">
                        overall_score
                      </code>{" "}
                      and an{" "}
                      <code className="font-mono text-[#101828]">elements</code>{" "}
                      array, each with{" "}
                      <code className="font-mono text-[#101828]">element</code>,{" "}
                      <code className="font-mono text-[#101828]">is_match</code>,{" "}
                      <code className="font-mono text-[#101828]">
                        match_percentage
                      </code>{" "}
                      (0–100), and{" "}
                      <code className="font-mono text-[#101828]">reason</code>)
                      and a{" "}
                      <code className="font-mono text-[#101828]">citations</code>{" "}
                      array of{" "}
                      <code className="font-mono text-[#101828]">
                        {`{ number, description, patentId }`}
                      </code>{" "}
                      objects.
                    </p>
                  </div>

                  {/* Right: code samples */}
                  <div className="space-y-4">
                    <CodeBlock
                      tabs={[
                        { label: "cURL", code: analyzeCurl },
                        { label: "Python", code: analyzePython },
                      ]}
                    />
                    <div>
                      <p className="text-xs font-interSemiBold text-[#475467] mb-1.5 uppercase tracking-wide">
                        200 Response
                      </p>
                      <CodeBlock
                        tabs={[{ label: "JSON", code: analyzeResponse }]}
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* Health check */}
              <section className="mb-6">
                <SectionHeading id="health">Health check</SectionHeading>
                <div className="flex items-center gap-x-2 mb-4">
                  <MethodBadge method="GET" />
                  <code className="text-sm font-mono text-[#101828]">
                    /IpVijil/citation/health
                  </code>
                  <span className="text-xs text-[#667085]">
                    · No auth required
                  </span>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 items-start">
                  <p className="text-sm text-[#475467] leading-relaxed">
                    Returns service health. Useful for liveness and readiness
                    checks. Does not require an API key.
                  </p>
                  <div className="space-y-4">
                    <CodeBlock
                      tabs={[
                        { label: "cURL", code: healthCurl },
                        { label: "Python", code: healthPython },
                      ]}
                    />
                    <div>
                      <p className="text-xs font-interSemiBold text-[#475467] mb-1.5 uppercase tracking-wide">
                        200 Response
                      </p>
                      <CodeBlock
                        tabs={[{ label: "JSON", code: healthResponse }]}
                      />
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiReference;
